// Navigate to the PE Tearsheet page for a company (F40.2).
import { paths } from '@/routes/paths';

const VAGUE_GEO = new Set([
  'africa',
  'antarctica',
  'asia',
  'europe',
  'north america',
  'oceania',
  'south america',
  'emea',
  'apac',
  'americas',
  'latin america',
  'middle east',
  'eu',
  'global',
  'worldwide',
  'international',
]);

export type TearsheetGeo = { country?: string; state?: string; geoHint?: string };

function isVagueGeo(value: string): boolean {
  return VAGUE_GEO.has(value.trim().toLowerCase());
}

/** HQ from a holdings row. Country/state win; geography blobs are a hint, not a guess. */
export function geoFromHolding(h: {
  country?: string | null;
  state?: string | null;
  city?: string | null;
  geography?: string | null;
}): TearsheetGeo | undefined {
  const country = h.country?.trim();
  const state = h.state?.trim();
  const city = h.city?.trim();
  const geography = h.geography?.trim();
  const hintParts = [city, geography && !isVagueGeo(geography) ? geography : undefined].filter(
    (p): p is string => Boolean(p),
  );
  const geoHint = hintParts.length ? hintParts.join(', ') : undefined;

  if (country) {
    return {
      country,
      ...(state ? { state } : {}),
      ...(!state && geoHint ? { geoHint } : {}),
    };
  }
  if (state) {
    return { geoHint: geoHint ? `${state}, ${geoHint}` : state };
  }
  if (geoHint) return { geoHint };
  return undefined;
}

/** HQ from a research-result row (Country / State / City / Geography columns). */
export function geoFromResultRow(row: Record<string, unknown>): TearsheetGeo | undefined {
  const pick = (...names: string[]) => {
    const want = new Set(names.map((n) => n.toLowerCase()));
    for (const [key, value] of Object.entries(row)) {
      if (!want.has(key.trim().toLowerCase())) continue;
      const text = String(value ?? '').trim();
      if (text) return text;
    }
    return undefined;
  };
  return geoFromHolding({
    country: pick('country'),
    state: pick('state', 'state/province', 'state / province'),
    city: pick('city'),
    geography: pick('geography', 'location', 'hq', 'headquarters'),
  });
}

export function buildTearsheetPath(
  companyName: string,
  website?: string,
  slide?: number,
  geo?: TearsheetGeo,
  id?: string,
): string {
  const params = new URLSearchParams({ company: companyName });
  if (id?.trim()) params.set('id', id.trim());
  if (website?.trim()) params.set('website', website.trim());
  if (geo?.country?.trim()) params.set('country', geo.country.trim());
  if (geo?.state?.trim()) params.set('state', geo.state.trim());
  if (geo?.geoHint?.trim()) params.set('geoHint', geo.geoHint.trim());
  if (slide != null && Number.isFinite(slide)) params.set('slide', String(slide));
  return `${paths.pe.tearsheet}?${params.toString()}`;
}

/** Returns the tearsheet URL path + query string. */
export function openTearsheet(companyName: string, website?: string, geo?: TearsheetGeo): string {
  return buildTearsheetPath(companyName, website, undefined, geo);
}

/** Open the request/composer tearsheet in a new browser tab. */
export function openTearsheetInNewTab(
  companyName: string,
  website?: string,
  geo?: TearsheetGeo,
): Window | null {
  return window.open(openTearsheet(companyName, website, geo), '_blank', 'noopener,noreferrer');
}

/** Full-page polished viewer (no app sidebar). */
export function buildTearsheetViewerPath(tearsheetId: string): string {
  return paths.pe.tearsheetView(tearsheetId);
}

/** Open the polished tearsheet viewer in a dedicated browser tab. */
export function openTearsheetViewer(tearsheetId: string): Window | null {
  return window.open(buildTearsheetViewerPath(tearsheetId), '_blank', 'noopener,noreferrer');
}
